package com.evoapps.evoshift.widgets

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.CloudOff
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.ui.DarkGold
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.asString
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.error_state_retry
import evoshift.composeapp.generated.resources.error_state_title
import org.jetbrains.compose.resources.stringResource

/** Test tag of [ErrorState], for robots. */
const val ERROR_STATE_TAG = "error_state"

/**
 * SH-168: a first load failed (the screen has nothing to show): what went wrong and "Try again",
 * instead of a screen that looks empty. Later failures (a save, a refresh over data already shown)
 * stay snackbars. It scrolls when it has a height of its own, so a pull-to-refresh around it works.
 */
@Composable
fun ErrorState(message: UiText, onRetry: () -> Unit, modifier: Modifier = Modifier) {
    ScrollableCentre(modifier.testTag(ERROR_STATE_TAG)) {
        Column(
            modifier = Modifier.widthIn(max = 420.dp).padding(horizontal = 24.dp, vertical = 48.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            Icon(Icons.Default.CloudOff, contentDescription = null, tint = DarkGold, modifier = Modifier.size(48.dp))
            Text(
                stringResource(Res.string.error_state_title),
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold,
                color = DeepNavy,
                textAlign = TextAlign.Center
            )
            Text(
                message.asString(),
                style = MaterialTheme.typography.bodyMedium,
                color = DeepNavy.copy(alpha = 0.75f),
                textAlign = TextAlign.Center
            )
            GoldButton(text = stringResource(Res.string.error_state_retry), onClick = onRetry, modifier = Modifier.padding(top = 8.dp))
        }
    }
}

/**
 * SH-168: [content] centred in the space it has, and scrollable there, so a pull-to-refresh
 * around an empty or failed screen still has something to pull. Inside a scrolling list (no height
 * of its own) it simply takes its content's height.
 */
@Composable
fun ScrollableCentre(modifier: Modifier = Modifier, content: @Composable () -> Unit) {
    BoxWithConstraints(modifier.fillMaxSize()) {
        val scroll = if (constraints.hasBoundedHeight) Modifier.verticalScroll(rememberScrollState()).heightIn(min = maxHeight) else Modifier
        Column(
            modifier = Modifier.fillMaxWidth().then(scroll),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center
        ) { content() }
    }
}
